<template>
  <div class="sticky-top search-control-panel">
    <div class="position-relative">
      <Panel
        v-if="!isLoading"
        :title="$i18n.t('search_control_panel_name')"
        :additional-title="filterBadge"
        :collapse="isCollapsed"
        @collapse-changed="handleChangedCollapse($event)"
      >
        <div
          class="search-control-container d-flex flex-nowrap align-items-start w-100 overflow-scroll"
          :class="{ 'square-bottom': searchOnButtonPress }"
        >
          <div class="search-field-container flex-grow-1" v-for="column in tableData" :key="column.prop">
            <div class="search-field-header d-flex align-items-center mb-1" @click="setOrder(column.prop)">
              <span class="me-1 column-label">{{ $i18n.t(column.label) }}</span>
              <span class="material-icons md-18 sort" :class="order[column.prop]">{{
                order[column.prop] ? 'sort' : ''
              }}</span>
              <sup>{{ getPriority(column.prop) }}</sup>
            </div>
            <search-control v-if="column.searchable" :column="column.prop" />
          </div>
        </div>
        <div class="search-buttons-container">
          <search-button @discard-button-pressed="collapsePanel()" @search-button-pressed="collapsePanel()" />
        </div>
      </Panel>
    </div>
  </div>
</template>

<script>
import SearchControl from './SearchControl'
import EventBus from '../../../utils/event-bus'
import { DEFAULT_PAGER } from '../lib/list-constants'
import { get, isEmpty, map } from 'lodash'
import { createStorage } from '../../../utils'
import Panel from '../../Panel.vue'
import SearchButton from './SearchButton.vue'

export default {
  name: 'SearchControlContainer',
  components: {
    SearchButton,
    SearchControl,
    Panel,
  },
  data() {
    return {
      isLoading: true,
      isCollapsed: false
    }
  },
  computed: {
    columns() {
      return this.$get('columns')
    },
    orderParams() {
      if (isEmpty(this.order)) return []
      return map(this.order, (value, key) => {
        return { column: key, dir: value }
      })
    },
    order: {
      get() {
        return this.$get('orderState')
      },
      set(column) {
        let direction = this.$get('orderState')[column]
        switch (direction) {
          case 'asc':
            direction = 'desc'
            break
          case 'desc':
            direction = null
            break
          default:
            direction = 'asc'
        }
        this.$dispatch('setOrder', { column, value: direction })
        this.$dispatch('setPager', {
          prop: 'offset',
          value: DEFAULT_PAGER.offset,
        })
        if (!this.orderParams.length) {
          this.localStore.removeItemValue('orderParams')
        } else {
          this.localStore.setItemValue('orderParams', this.orderParams)
        }
        EventBus.$emit(`apply-filter-${this.id}`, { id: this.id, type: 'button' })
      },
    },
    panelId() {
      return this.$get('panelId')
    },
    id() {
      return this.$get('id')
    },
    searchOnButtonPress() {
      return get(this.$get('extendedOptions'), 'searchKeypress', false)
    },
    searchBarIsCollapsed() {
      return this.$get('searchBarIsCollapsed')
    },
    tableData() {
      return this.columns.map((column) => {
        return {
          label: column.title,
          prop: column.data,
          searchable: column.searchable,
        }
      })
    },
    searchParams() {
      return this.$get('searchParams') || []
    },
    searchParamKeys() {
      return Object.keys(this.searchParams) || []
    },
    numberOfSearchParams() {
      const columns = this.tableData.map((column) => column.prop)
      return columns.filter((column) => this.searchParamKeys.includes(column)).length
    },
    filterBadge() {
      return `<div class="el-badge align-text-top ms-3" style="top: 2px;"><span class="material-icons">filter_alt</span><sup class="el-badge__content is-fixed">${this.numberOfSearchParams}</sup></div>`
    },
  },
  methods: {
    getPriority(column) {
      return this.orderParams.findIndex((orderParam) => column === orderParam.column) + 1 || ''
    },
    setOrder(column) {
      this.order = column
    },
    collapsePanel() {
      this.isCollapsed = !this.isCollapsed
    },
    handleChangedCollapse(isCollapsed) {
      if (this.isCollapsed !== isCollapsed) {
        this.isCollapsed = isCollapsed
        this.$dispatch('setSearchBarCollapsed', { value: isCollapsed })
        this.localStore.setItemValue('searchBarIsCollapsed', isCollapsed)
      }
    },
  },
  mounted() {
    this.isCollapsed = this.localStore.getItemValue('searchBarIsCollapsed') ?? this.searchBarIsCollapsed ?? false
    this.isLoading = false
  },
  created() {
    this.localStore = createStorage(this.$get('storageKey'))
  },
}
</script>

<style lang="scss" scoped>
.search-control-container {
  top: 48px;
  .search-field-container {
    min-width: 200px;
    max-width: 20%;
    margin: 4px;
  }
}
.search-buttons-container {
  margin-top: 10px;
}

@media (max-width: 768px) {
  .search-field-container {
    min-width: unset !important;
    max-width: unset !important;
    width: 100%;
    margin: unset !important;
  }
  .search-control-container {
    flex-direction: column;
    max-height: 400px;
  }
  .search-control-panel {
    top: 0 !important;
  }
}

.search-control-panel {
  top: $top-bar-height;
}

.search-field-header {
  cursor: pointer;
  line-height: 23px;
  font-weight: bold;
}
.sort.asc {
  transform: rotateX(180deg);
}
.square-bottom {
  border-radius: $border-radius $border-radius 0 0;
}
</style>
